import React, { createContext, ReactNode, useMemo, useState } from "react";
import { CareRecipientSortCondition, CareRecipientWithDataSyncInformation } from "../../domain/care_recipient/CareRecipientWithDataSyncInformation";
import { DateTime } from "../../domain/fundamental/DateTime";
import { User } from "../../domain/user/User";
import { compareString } from "../funcs/compare";
import { useList } from "../hooks/admin/useList";

type ContextType = {
    hideDataSyncFinished: boolean,
    setHideDataSyncFinished: (hideDataSyncFinished: boolean) => void,
    searchKeyword: string,
    setSearchKeyword: (searchKeyword: string) => void,
    sortCondition: CareRecipientSortCondition | undefined,
    setSortCondition: (sortCondition: CareRecipientSortCondition) => void,
    sortAsc: boolean,
    setSortAsc: (sortAsc: boolean) => void,
    houseIdPrefixFilters: string[],
    setHouseIdPrefixFilters: (houseIdPrefixFilters: string[]) => void,
    userRoleFilters: string[],
    setUserRoleFilters: (houseIdPrefixFilters: string[]) => void,
    displayList: {
        careRecipientWithDataSyncInfo: CareRecipientWithDataSyncInformation | null,
        users: User[]
    }[] | null
}

const defaultContextValue: ContextType = {
    hideDataSyncFinished: false,
    setHideDataSyncFinished: (_: boolean) => {},
    searchKeyword: "",
    setSearchKeyword: (_: string) => {},
    sortCondition: undefined,
    setSortCondition: (_: CareRecipientSortCondition) => {},
    sortAsc: false,
    setSortAsc: (_: boolean) => {},
    houseIdPrefixFilters: [],
    setHouseIdPrefixFilters: (_: string[]) => {},
    userRoleFilters: [],
    setUserRoleFilters: (_: string[]) => {},
    displayList: []
};

export const AdminListContext = createContext(defaultContextValue);

export const AdminListProvider = React.memo(function AdminListProvider(props: Props) {
    const [hideDataSyncFinished, setHideDataSyncFinished] = useState<boolean>(false);

    const [searchKeyword, setSearchKeyword] = useState("");

    const [sortCondition, setSortCondition] = useState<CareRecipientSortCondition>();

    const [sortAsc, setSortAsc] = useState<boolean>(false);

    const [houseIdPrefixFilters, setHouseIdPrefixFilters] = useState<string[]>([]);

    const [userRoleFilters, setUserRoleFilters] = useState<string[]>([]);

    const list = useList();

    const displayList = useMemo(() => {
        if (!list) return list;

        let filteredAndSortedList = searchKeyword.length ? list.slice().filter(obj => {
            const careRecipient = obj.careRecipientWithDataSyncInfo;
            const users = obj.users;
            const careRecipientContainsKeyword =
                careRecipient?.careRecipient.name.value.includes(searchKeyword) || careRecipient?.careRecipient.houseId?.value.includes(searchKeyword);
            const usersContainsKeyword = users.some((user) => {
                return user.name.value.includes(searchKeyword) || user.email.value.includes(searchKeyword);
            });
            return careRecipientContainsKeyword || usersContainsKeyword;
        }) : list.slice();

        if (hideDataSyncFinished) {
            filteredAndSortedList = filteredAndSortedList.filter(item => {
                const now = DateTime.now().format();
                return !item.careRecipientWithDataSyncInfo?.dataSyncEndTime
                    || item.careRecipientWithDataSyncInfo?.dataSyncEndTime.format() > now;
            });
        }

        if (houseIdPrefixFilters.length) {
            filteredAndSortedList = filteredAndSortedList.filter(item => (
                houseIdPrefixFilters.filter(houseIdPrefix => item.careRecipientWithDataSyncInfo?.careRecipient.houseId?.value.startsWith(houseIdPrefix)).length
            ));
        }

        if (userRoleFilters.length) {
            filteredAndSortedList = filteredAndSortedList.filter(item => {
                const filteredUsers = item.users.filter(user => userRoleFilters.includes(user.role as string));
                return filteredUsers.length > 0;
            }).map(item => {
                const filteredUsers = item.users.filter(user => userRoleFilters.includes(user.role as string));
                return { careRecipientWithDataSyncInfo: item.careRecipientWithDataSyncInfo, users: filteredUsers };
            });
        }

        if (sortCondition) {
            filteredAndSortedList.sort((firstData, secondData) => {
                if (sortCondition === CareRecipientSortCondition.HOUSE_ID
                    || sortCondition === CareRecipientSortCondition.CARE_RECIPIENT_NAME) {
                    const compareDataFirst = firstData.careRecipientWithDataSyncInfo?.careRecipient[sortCondition]?.value;
                    const compareDataSecond = secondData.careRecipientWithDataSyncInfo?.careRecipient[sortCondition]?.value;
                    return compareString(sortAsc ? "asc" : "desc", compareDataFirst, compareDataSecond);
                } else {
                    const compareDataFirst = firstData.careRecipientWithDataSyncInfo ? firstData.careRecipientWithDataSyncInfo[sortCondition]?.format() : "";
                    const compareDataSecond = secondData.careRecipientWithDataSyncInfo ? secondData.careRecipientWithDataSyncInfo[sortCondition]?.format() : "";
                    return compareString(sortAsc ? "asc" : "desc", compareDataFirst, compareDataSecond);
                }
            });
        }

        return filteredAndSortedList;
    }, [list, hideDataSyncFinished, houseIdPrefixFilters, searchKeyword, sortAsc, sortCondition, userRoleFilters]);

    const value = useMemo(() => ({
        hideDataSyncFinished: hideDataSyncFinished,
        setHideDataSyncFinished: setHideDataSyncFinished,
        searchKeyword: searchKeyword,
        setSearchKeyword: setSearchKeyword,
        sortCondition: sortCondition,
        setSortCondition: setSortCondition,
        sortAsc: sortAsc,
        setSortAsc: setSortAsc,
        houseIdPrefixFilters: houseIdPrefixFilters,
        setHouseIdPrefixFilters: setHouseIdPrefixFilters,
        userRoleFilters: userRoleFilters,
        setUserRoleFilters: setUserRoleFilters,
        displayList: displayList
    }), [hideDataSyncFinished, searchKeyword, sortCondition, sortAsc, houseIdPrefixFilters, userRoleFilters, displayList]);

    return (
        <AdminListContext.Provider value={value}>{props.children}</AdminListContext.Provider>
    );
});

type Props = {
    children: ReactNode
};
